import React, {Fragment, useContext, useEffect, useState} from "react";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import DataTable from "react-data-table-component";
import Loader from "../../../../../Layout/Loader";
import IconBtn from "../../../../../CommonElements/IconButton";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import WalletContext from "../../Context";
import type {WalletContextType} from "../../Context/WalletContext.types";
import {Button} from "reactstrap";
import {FaCheck} from "react-icons/fa";
import SweetAlert from "sweetalert2";
import MultimediaService from "../../../../SystemCore/Multimedia/Services/Multimedia.service";

interface walletTransactionsTableProps {
    params: any,
    tableOptions?: any,
    callBack?: () => void,
}

const WalletTransactionsTable = React.forwardRef(({
                                                      params,
                                                      callBack,
                                                      tableOptions = {delete: true, view: true}
                                                  }: walletTransactionsTableProps, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState(false);
    const [walletTransactions, setWalletTransactions] = useState<Array<any>>([])
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler, hasPermission} = authContext || {};
    const {getTransactionInfo, getOwerTypeColor} = walletContext || {};

    React.useImperativeHandle(ref, () => ({
        getWalletTransactions,
        addWalletTransaction
    }));

    const getWalletTransactions = (page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};
        axiosConfig
            .get(getApplicationRoute('wallet-transaction'), config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setWalletTransactions(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    useEffect(() => {
        getWalletTransactions(1, perPage);
    }, [params, perPage])

    const deleteWalletTransaction = (id: any) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getApplicationRoute('wallet-transaction')}/${id}`, {})
            .then((response: { data: { data: { id: any; }; }; }) => {
                if (response?.data?.data) {
                    setWalletTransactions(current => current.filter(user => {
                        return user.id !== response?.data?.data?.id;
                    }));
                }
                callBack && callBack();
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const confirmTransaction = (id: any) => {
        setLoaderVisibility(true);
        axiosConfig
            .post(`${getApplicationRoute('wallet-transaction')}/${id}/confirm`, {})
            .then((response: { data: { data: any; }; }) => {
                if (response?.data?.data) {
                    // Remove the confirmed transaction from the list
                    setWalletTransactions(current => current.filter(transaction => {
                        return transaction.id !== response?.data?.data?.id;
                    }));
                }
                callBack && callBack();
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const openConfirmAlert = (transactionId: any) => {
        SweetAlert.fire({
            title: 'Confirm Payment?',
            text: 'Are you sure you want to confirm this payment information?',
            icon: 'question',
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: 'Yes, Confirm',
            cancelButtonText: 'Cancel',
        })
            .then((result) => {
                if (result.isConfirmed) {
                    confirmTransaction(transactionId);
                }
            })
    }

    const addWalletTransaction = (walletTransaction: { id: any; }) => {
        const walletTransactionList = [...walletTransactions];
        const existingWalletTransactionIndex = walletTransactionList.findIndex(existingWalletTransaction => {
            return existingWalletTransaction.id === walletTransaction.id;
        });
        if (existingWalletTransactionIndex !== -1) {
            walletTransactionList[existingWalletTransactionIndex] = walletTransaction;
        } else {
            walletTransactionList.push(walletTransaction);
        }
        setWalletTransactions(walletTransactionList);
    }

    const getMultimediaList = (walletTransaction: any): Array<any> => {
        if (!Array.isArray(walletTransaction?.multimedia)) {
            return [];
        }

        return walletTransaction.multimedia.filter((item: any) => item && typeof item === 'object');
    }

    const viewAttachment = async (multimedia: any) => {
        try {
            await MultimediaService.viewMultimedia(multimedia);
        } catch (exception: any) {
            errorHandler(exception);
        }
    }

    const downloadAttachment = async (multimedia: any) => {
        try {
            await MultimediaService.downloadMultimedia(multimedia);
        } catch (exception: any) {
            errorHandler(exception);
        }
    }

    const walletTransactionTableColumns = [
        {
            name: 'ID',
            selector: (walletTransaction: any) => walletTransaction?.id,
            sortable: false,
            width: '60px',
        },
        {
            name: 'Wallet Owner',
            selector: (walletTransaction: any) => walletTransaction?.wallet_owner_name || '',
            sortable: false,
            grow: 1,
            cell: (walletTransaction: any) => (
                <div>
                    <span className={`badge badge-light-${getOwerTypeColor(walletTransaction?.wallet_owner_type || 'unknown')} m-r-20`}>
                        {walletTransaction?.wallet_owner_type || 'unknown'}
                    </span>
                    <span>{walletTransaction?.wallet_owner_name || '-'}</span>
                </div>
            ),
        },
        {
            name: 'Description',
            selector: (walletTransaction: any) => walletTransaction?.description,
            sortable: false,
            grow: 1,
        },
        {
            name: 'Transaction',
            selector: (walletTransaction: any) => walletTransaction?.amount,
            sortable: true,
            center: true,
            cell: (walletTransaction: any) => (
                <div>
                    <p>{getTransactionInfo(walletTransaction)}</p>
                    {walletTransaction.parent_transaction ?
                        <p>{getTransactionInfo(walletTransaction.parent_transaction)}</p> : null}
                    {walletTransaction.child_transaction ?
                        <p>{getTransactionInfo(walletTransaction.child_transaction)}</p> : null}
                </div>
            ),
        },
        {
            name: 'Attachment',
            sortable: false,
            grow: 1,
            cell: (walletTransaction: any) => {
                const multimediaList = getMultimediaList(walletTransaction);

                if (!multimediaList.length) {
                    return <span>-</span>;
                }

                return (
                    <div className='d-flex flex-column gap-1'>
                        {multimediaList.map((multimedia: any) => (
                            <div key={multimedia?.id || multimedia?.public_url || multimedia?.path} className='d-flex align-items-center gap-2'>
                                <Button color='link' className='p-0' onClick={() => viewAttachment(multimedia)}>
                                    {MultimediaService.resolveFileName(multimedia)}
                                </Button>
                                <Button color='link' className='p-0 text-muted' onClick={() => downloadAttachment(multimedia)}>
                                    Download
                                </Button>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            name: 'Date',
            selector: (walletTransaction: any) => walletTransaction?.date,
            sortable: false,
        },
        {
            name: 'Option',
            cell: (walletTransaction: any) => (
                <div className='d-flex align-items-right gap-2'>
                    {tableOptions?.confirm && !walletTransaction?.confirmed_at &&
                        hasPermission && hasPermission('update-wallet-transaction') &&
                        <Button
                            onClick={() => openConfirmAlert(walletTransaction?.id)}
                            color={`btn sm icon btn-outline-success`}
                            style={{padding: '0.375rem 0.75rem'}}
                            title="Confirm Payment"
                        >
                            <FaCheck style={{marginBottom: -2}}/>
                        </Button>
                    }
                    {tableOptions?.delete &&
                        <IconBtn confirm={{confirm:true}} onclick={() => deleteWalletTransaction(walletTransaction?.id)}
                                 type={'delete'} permission={'delete-wallet-transaction'}/>}
                </div>
            ),
            sortable: false,
            right: true,
            width: '80px',
        }
    ];
    

    return <Fragment>
        <Loader visibility={loaderVisibility}/>
        <DataTable pagination paginationServer columns={walletTransactionTableColumns}
                   data={walletTransactions}
                   paginationTotalRows={totalRows}
                   paginationPerPage={perPage}
                   paginationDefaultPage={currentPage}
                   onChangePage={(page) => getWalletTransactions(page, perPage)}
                   onChangeRowsPerPage={(newPerPage, page) => {
                       setPerPage(newPerPage);
                       getWalletTransactions(page, newPerPage);
                   }}
                   highlightOnHover={true}
                   striped={true} responsive={true}/>
    </Fragment>
});
export default WalletTransactionsTable;
